import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import {connect} from 'react-redux';
import {Field, reduxForm, formValueSelector} from 'redux-form';

import {selectModal} from 'ducks/modals';
import {
  roomMove,
  roomMoveFlow,
  moveEntireBooking as moveEntireBookingAction,
  selectBookingRoomTypeRef,
  selectTenancy,
  getMoveRoomOverlap
} from 'ducks/tenancy';
import {fetchAvailableRooms} from 'ducks/rooms';

import {required, validHour} from 'utils/validators';
import {formatDateForApi, formatDate} from 'utils/datetime';
import {headOption} from 'utils/common';

import {
  TextField,
  NumberField,
  CheckBoxField,
  SearchRoomField,
  SelectRoomTypeField,
  DateTimeField
} from 'components/Fields';
import {buildRoomSelectizeOption} from 'components/Fields/SearchRoomField';
import {buildRoomTypeSelectizeOption} from 'components/Fields/SelectRoomTypeField';
import FormField from 'components/FormField';
import ErrorBlock from 'components/ErrorBlock';
import Label from 'components/Label';
import Warning from 'components/Warning';

import {Overlap} from './components';
import {checkForNotMatchingRoomPurposesToBookingType} from 'ducks/allocations/utils';

import './styles.css';

const DEFAULT_CLEANUP_PERIOD_DURATION = 3;
const DIFFERENT_ROOM_TYPE_IS_SELECTED =
  "You have selected different room type to the member's current room type!";
const NOT_CORRESPONDING_ROOM_PURPOSE_TYPE_TO_BOOKING_TYPE =
  'You have selected room with purpose type that does not correspond to booking type!';

const DEFAULT_START_TIME = '11:00';
const DEFAULT_END_TIME = '15:00';

const formSubmit = (values, dispatch, props) => {
  const {tenancy: {bookingId}, locationRef} = props;
  const {
    newRoom,
    moveEntireBooking,
    createCleanup,
    cleanupPeriodDays,
    currentRoomAllocation,
    moveInDate,
    moveInHour,
    vacateDate,
    vacateHour,
    reason
  } = values;
  const extraActions = props.getExtraActions();

  if (moveEntireBooking) {
    const data = {
      locationRef,
      booking: bookingId,
      newRoom: newRoom.value,
      createCleanup,
      cleanupPeriodDays
    };

    dispatch(moveEntireBookingAction(data, extraActions));
  } else {
    const data = {
      room: newRoom.value,
      booking: bookingId,
      allocation: currentRoomAllocation.id,
      moveInDate: formatDateForApi(moveInDate),
      moveInHour,
      vacateDate: formatDateForApi(vacateDate),
      vacateHour,
      cleanupPeriodDays,
      locationRef,
      reason
    };

    dispatch(roomMove(data, extraActions));
  }
};

const validateRoomType = (value, allValues, _props, _name) => {
  const newRoomTypeRef = _.get(value, 'object.ref', null);
  const currentAllocationRoomTypeRef = _.get(
    allValues,
    'currentRoomAllocation.room.roomType',
    null
  );

  if (_.isNil(newRoomTypeRef) || _.isNil(currentAllocationRoomTypeRef)) {
    return undefined;
  }
  if (newRoomTypeRef !== currentAllocationRoomTypeRef) {
    return DIFFERENT_ROOM_TYPE_IS_SELECTED;
  }

  return undefined;
};

class RoomMoveForm extends React.Component<any> {
  componentDidMount() {
    const {initialValues: {moveInDate, moveInHour}} = this.props;

    this.fetchFormData({moveInDate, moveInHour});
  }

  fetchFormData = ({moveInDate, moveInHour}: any) => {
    const {tenancy: {bookingId}, roomType, locationRef, modalName} = this.props;

    const startHour = moveInHour ? moveInHour : this.props.moveInHour;

    this.props.roomMoveFlow({
      locationRef,
      bookingId,
      roomType: _.get(roomType, 'value'),
      startDate: formatDateForApi(moveInDate),
      startHour: startHour,
      endDate: this.getFetchAvailableRoomsEnd(),
      modalName,
      cleanupPeriodDays: this.getFetchAvailableRoomsCleanupPeriodDays()
    });
  };

  getFetchAvailableRoomsExtraActions = () => [
    ...this.props.storeDataExtraActions(data => ({
      newRooms: data
    }))
  ];

  onCreateCleanupChange = event => {
    const createCleanup = event.target.checked;

    const cleanupPeriodDays = createCleanup
      ? DEFAULT_CLEANUP_PERIOD_DURATION
      : null;

    this.props.change('cleanupPeriodDays', cleanupPeriodDays);
    this.props.resetSection('newRoom');

    let {locationRef, roomType, moveInHour} = this.props;

    this.props.fetchAvailableRooms(
      {
        locationRef,
        roomType: _.get(roomType, 'value'),
        startDate: this.getFetchAvailableRoomsStart(),
        startHour: moveInHour,
        endDate: this.getFetchAvailableRoomsEnd(),
        cleanupPeriodDays
      },
      this.getFetchAvailableRoomsExtraActions()
    );
  };

  onCleanupPeriodDaysChange = event => {
    const cleanupPeriodDays = event.target.value;
    if (cleanupPeriodDays === '') {
      return;
    }

    let {locationRef, roomType, moveInHour} = this.props;
    this.props.resetSection('newRoom');

    this.props.fetchAvailableRooms(
      {
        locationRef,
        roomType: _.get(roomType, 'value'),
        startDate: this.getFetchAvailableRoomsStart(),
        startHour: moveInHour,
        endDate: this.getFetchAvailableRoomsEnd(),
        cleanupPeriodDays: _.parseInt(cleanupPeriodDays)
      },
      this.getFetchAvailableRoomsExtraActions()
    );
  };

  onRoomTypeChange = value => {
    const {locationRef, moveInHour} = this.props;

    this.props.resetSection('newRoom');
    this.props.fetchAvailableRooms(
      {
        locationRef,
        roomType: value.value,
        startDate: this.getFetchAvailableRoomsStart(),
        startHour: moveInHour,
        endDate: this.getFetchAvailableRoomsEnd(),
        cleanupPeriodDays: this.getFetchAvailableRoomsCleanupPeriodDays()
      },
      this.getFetchAvailableRoomsExtraActions()
    );
  };

  getFetchAvailableRoomsStart = () => {
    const {
      moveEntireBooking,
      moveInDate,
      tenancy: {period: {startDate}}
    } = this.props;

    if (moveEntireBooking) {
      return formatDateForApi(formatDate(startDate));
    }
    return formatDateForApi(moveInDate);
  };

  getFetchAvailableRoomsCleanupPeriodDays = () => {
    const {createCleanup, cleanupPeriodDays} = this.props;

    return createCleanup ? _.parseInt(cleanupPeriodDays) : null;
  };

  getFetchAvailableRoomsEnd = () => {
    const endDate: any = this.props.tenancy.period.endDate;

    return formatDateForApi(formatDate(endDate));
  };

  getMoveRoomOverlap = ({moveInDate, moveInHour, vacateDate, vacateHour}) => {
    const {locationRef} = this.props;

    const successExtraActions = this.props.storeDataExtraActions(data => ({
      overlap: data.overlap,
      warnings: null
    }));
    const failureExtraActions = this.props.storeDataExtraActions(
      ({warnings}) => ({
        warnings,
        overlap: null
      })
    );

    this.props.getMoveRoomOverlap(
      {
        locationRef,
        moveInDate: formatDateForApi(moveInDate),
        moveInHour,
        vacateDate: formatDateForApi(vacateDate),
        vacateHour
      },
      successExtraActions,
      failureExtraActions
    );
  };
  onMoveInDateChange = moveInDate => {
    this.props.resetSection('newRoom');

    this.fetchFormData({moveInDate});
    this.props.change('vacateDate', moveInDate);

    const {moveInHour, vacateHour} = this.props;
    this.getMoveRoomOverlap({
      moveInDate,
      moveInHour,
      vacateDate: moveInDate,
      vacateHour
    });
  };

  onMoveInHourChange = event => {
    const moveInHour = event.target.value;

    // TODO: This check for valid hour will be removed when we indtroduce hourField
    if (!_.isNil(validHour(moveInHour))) {
      return;
    }
    let {moveInDate, vacateDate, vacateHour} = this.props;

    this.props.resetSection('newRoom');

    this.getMoveRoomOverlap({moveInDate, moveInHour, vacateDate, vacateHour});

    this.fetchFormData({moveInDate, moveInHour});
  };

  onVacateDateChange = vacateDate => {
    const {moveInDate, moveInHour, vacateHour} = this.props;
    this.getMoveRoomOverlap({moveInDate, moveInHour, vacateDate, vacateHour});
  };

  onVacateHourChange = event => {
    const vacateHour = event.target.value;

    // TODO: This check for valid hour will be removed when we indtroduce hourField
    if (!_.isNil(validHour(vacateHour))) {
      return;
    }
    const {moveInDate, moveInHour, vacateDate} = this.props;
    this.getMoveRoomOverlap({moveInDate, moveInHour, vacateDate, vacateHour});
  };

  onMoveEntireBookingChange = event => {
    const onMoveEntireBooking = event.target.checked;
    let {
      locationRef,
      roomType,
      moveInDate,
      moveInHour,
      tenancy: {period: {startDate}}
    } = this.props;

    startDate = onMoveEntireBooking
      ? formatDateForApi(formatDate(startDate))
      : formatDateForApi(moveInDate);

    this.props.fetchAvailableRooms(
      {
        locationRef,
        roomType: _.get(roomType, 'value'),
        startDate,
        startHour: moveInHour,
        endDate: this.getFetchAvailableRoomsEnd(),
        cleanupPeriodDays: this.getFetchAvailableRoomsCleanupPeriodDays()
      },
      this.getFetchAvailableRoomsExtraActions()
    );

    this.props.resetSection('reason');
  };
  render() {
    const {
      error,
      newRooms,
      roomTypes,
      moveInDate,
      moveEntireBooking,
      overlapWarning,
      overlap,
      createCleanup,
      checkedInAt,
      bookingType,
      newRoomPurposesForPeriod
    } = this.props;

    const hasNotMatchingRoomPurposeToBookingType = checkForNotMatchingRoomPurposesToBookingType(
      {roomPurposesForPeriod: newRoomPurposesForPeriod, bookingType}
    );
    return (
      <form className="RoomMoveForm">
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <Field
          name="moveEntireBooking"
          component={CheckBoxField}
          label="Move entire booking"
          onChange={this.onMoveEntireBookingChange}
          disabled={!_.isNull(checkedInAt)}
        />
        <FormField>
          <Label>Move into new room</Label>
          <DateTimeField
            dateName="moveInDate"
            dateDisabled={moveEntireBooking}
            onDateChange={this.onMoveInDateChange}
            onTimeChange={this.onMoveInHourChange}
            timeName="moveInHour"
            timeDisabled={moveEntireBooking}
          />
        </FormField>
        <FormField>
          <Label>Vacate old room</Label>
          <DateTimeField
            dateName="vacateDate"
            dateDisabled={moveEntireBooking}
            onDateChange={this.onVacateDateChange}
            onTimeChange={this.onVacateHourChange}
            timeName="vacateHour"
            timeDisabled={moveEntireBooking}
          />
        </FormField>

        {overlap && <Overlap overlap={overlap} />}
        {overlapWarning && (
          <Warning className="overlapWarning">{overlapWarning}</Warning>
        )}

        <FormField>
          <Label>Current room allocation</Label>
          <Field
            name="currentRoomAllocation"
            component={TextField}
            disabled={true}
            format={cra => _.get(cra.room, 'roomNumber', '')}
          />
        </FormField>
        <Field
          name="createCleanup"
          component={CheckBoxField}
          onChange={this.onCreateCleanupChange}
          label="Include a cleanup period"
          className="createCleanup"
        />
        {createCleanup && (
          <FormField>
            <Label>Cleanup period duration</Label>
            <Field
              name="cleanupPeriodDays"
              validate={[required]}
              component={NumberField}
              onChange={this.onCleanupPeriodDaysChange}
              placeholder="cleanup days"
              min={1}
              step={1}
              hidden={true}
            />
          </FormField>
        )}
        <FormField className="roomTypeField">
          <Label>Room type</Label>
          <SelectRoomTypeField
            groupedRoomTypes={roomTypes}
            disabled={!moveInDate}
            onChange={this.onRoomTypeChange}
            warn={[validateRoomType]}
          />
        </FormField>
        <FormField>
          <Label>New room</Label>
          <SearchRoomField
            name="newRoom"
            rooms={newRooms}
            validate={[required]}
          />
        </FormField>

        {hasNotMatchingRoomPurposeToBookingType && (
          <Warning className="notCorrespondingRoomPurposeTypeWarning">
            {NOT_CORRESPONDING_ROOM_PURPOSE_TYPE_TO_BOOKING_TYPE}
          </Warning>
        )}

        <FormField>
          <Label>Reason</Label>
          <Field
            name="reason"
            component={TextField}
            placeholder="Room Move Reason"
            disabled={moveEntireBooking}
          />
        </FormField>
      </form>
    );
  }
}

const mapStateToProps = state => {
  const modal = selectModal(state, 'roomMove');
  const formSelector = formValueSelector('roomMove');

  const currentRoomAllocation = _.get(modal, 'data.currentRoomAllocation', {});
  const newRooms = _.get(modal, 'data.newRooms', []);

  const currentBookingRoomTypeRef = selectBookingRoomTypeRef(state);
  const groupedRoomTypes = _.get(modal, 'data.roomTypes', []);
  let roomTypes = _.flatten(
    groupedRoomTypes.map(roomTypeGroup => roomTypeGroup.roomTypes)
  );
  // Move room type at first place in order to visualize it by default
  roomTypes = _.sortBy(roomTypes, (roomType: any) => {
    return roomType.ref === currentBookingRoomTypeRef ? 0 : 1;
  });

  const newRoom = headOption(newRooms, buildRoomSelectizeOption);
  const roomTypeInitial = headOption(roomTypes, buildRoomTypeSelectizeOption);

  const tenancy = selectTenancy(state);

  const cleanupPeriodDays = formSelector(state, 'cleanupPeriodDays');

  const overlapWarning = _.get(modal, 'data.warnings._warning[0]');
  const overlap = _.get(modal, 'data.overlap');

  return {
    initialValues: {
      moveEntireBooking: false,
      newRoom,
      roomType: roomTypeInitial,
      currentRoomAllocation,
      cleanupPeriodDays: DEFAULT_CLEANUP_PERIOD_DURATION,
      moveInHour: DEFAULT_START_TIME,
      moveInDate: formatDate(moment()),
      vacateDate: formatDate(moment()),
      vacateHour: DEFAULT_END_TIME,
      createCleanup: true
    },
    newRooms,
    roomTypes: groupedRoomTypes,
    currentRoomAllocation,
    moveEntireBooking: formSelector(state, 'moveEntireBooking'),
    tenancy,
    roomType: formSelector(state, 'roomType'),
    moveInDate: formSelector(state, 'moveInDate'),
    moveInHour: formSelector(state, 'moveInHour'),
    vacateDate: formSelector(state, 'vacateDate'),
    vacateHour: formSelector(state, 'vacateHour'),
    cleanupPeriodDays: cleanupPeriodDays || DEFAULT_CLEANUP_PERIOD_DURATION,
    createCleanup: formSelector(state, 'createCleanup'),
    overlapWarning,
    overlap,
    newRoomPurposesForPeriod: _.get(
      formSelector(state, 'newRoom'),
      'object.roomPurposesForPeriod',
      []
    )
  };
};
const mapDispatchToProps = {
  roomMoveFlow,
  fetchAvailableRooms,
  getMoveRoomOverlap
};
const moveRoomForm = reduxForm({
  form: 'roomMove',
  onSubmit: formSubmit,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true
})(RoomMoveForm);
export default connect(mapStateToProps, mapDispatchToProps)(moveRoomForm);
